import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { SettingsProvider } from '@/lib/settings';
import Header from '@/components/site/Header';
import Footer from '@/components/site/Footer';
import SmoothScroll from '@/components/site/SmoothScroll';
import CustomCursor from '@/components/site/CustomCursor';
import SoundToggle from '@/components/site/SoundToggle';
import Loader from '@/components/site/Loader';

import Home from '@/pages/Home';
import About from '@/pages/About';
import Services from '@/pages/Services';
import ServiceDetail from '@/pages/ServiceDetail';
import Portfolio from '@/pages/Portfolio';
import CaseStudy from '@/pages/CaseStudy';
// Pricing hidden for now — restore this import and the /pricing route below.
// import Pricing from '@/pages/Pricing';
import Blog from '@/pages/Blog';
import BlogDetail from '@/pages/BlogDetail';
import Contact from '@/pages/Contact';
import NotFound from '@/pages/NotFound';
// import TestimonialsPage from '@/pages/TestimonialsPage'; // disabled until real testimonials exist — uncomment + re-add the route below when ready

import AdminLogin from '@/pages/admin/AdminLogin';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminServices from '@/pages/admin/AdminServices';
import AdminProjects from '@/pages/admin/AdminProjects';
import AdminTestimonials from '@/pages/admin/AdminTestimonials';
import AdminPosts from '@/pages/admin/AdminPosts';
import AdminPricing from '@/pages/admin/AdminPricing';
import AdminFaqs from '@/pages/admin/AdminFaqs';
import AdminLeads from '@/pages/admin/AdminLeads';
import AdminNewsletter from '@/pages/admin/AdminNewsletter';
import AdminSettings from '@/pages/admin/AdminSettings';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/portfolio/:slug" element={<CaseStudy />} />
          {/* <Route path="/pricing" element={<Pricing />} /> */}
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/contact" element={<Contact />} />
          {/* <Route path="/testimonials" element={<TestimonialsPage />} /> */}

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="posts" element={<AdminPosts />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="faqs" element={<AdminFaqs />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <SoundToggle />}
    </>
  );
}

function App() {
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (document.hidden) return false;
    // Allow disabling via query string for automated tests / preview
    try { if (new URLSearchParams(window.location.search).has('noloader')) return false; } catch (e) { /* noop */ }
    if (sessionStorage.getItem('sf_visited')) return false;
    sessionStorage.setItem('sf_visited', '1');
    return true;
  });
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setLoading(false), 2200);
    return () => clearTimeout(t);
  }, [loading]);

  return (
    <HelmetProvider>
      <SettingsProvider>
        <BrowserRouter>
          <SmoothScroll />
          <CustomCursor />
          {loading && <Loader onDone={() => setLoading(false)} />}
          <Shell />
          <Toaster position="bottom-center" richColors closeButton />
        </BrowserRouter>
      </SettingsProvider>
    </HelmetProvider>
  );
}

export default App;
